import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, output, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { when } from 'app/common/format/vz-format';
import { TPipe } from 'app/common/i18n/t.pipe';
import { TextService } from 'app/common/i18n/text.service';
import { search, SearchGroupCode, SearchItem, SearchLabels, SEARCH_MIN } from 'app/core/panel/search.calc';
import { PanelStore } from 'app/core/panel/panel-store.service';
import { VzIconComponent } from './vz-icon.component';
import { VzIconName } from './vz-icons';
import { VzOwnerComponent } from 'app/component/vz/vz-owner.component';

const ICONS: Record<SearchGroupCode, VzIconName> = {
    vehicles: 'car',
    policies: 'shield',
    financing: 'cash',
    cases: 'folder',
    tickets: 'chat',
    documents: 'doc',
};

/**
 * Wyszukiwarka w pasku (Issue 14): pole „Szukaj: model, nr rej., VIN, nr polisy…”, od 2 znaków wyniki
 * pogrupowane (Pojazdy · Polisy · Umowy · Sprawy · Dokumenty, maks. 4 w grupie). Combobox ARIA: ↑ ↓ wybór,
 * Enter otwiera wybrany albo pierwszy wynik, Esc zamyka. Szuka w danych klienta, które panel już ma.
 */
@Component({
    selector: 'vz-global-search',
    standalone: true,
    imports: [VzOwnerComponent, RouterLink, TPipe, VzIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'vz-gsearch', '(document:click)': 'onDocumentClick($event)', '(focusout)': 'onFocusOut($event)' },
    template: `
        <div class="vz-gsearch__box">
            <vz-icon name="search" size="s" />
            <input
                #input
                type="search"
                role="combobox"
                autocomplete="off"
                aria-autocomplete="list"
                aria-controls="vz-gsearch-list"
                [attr.aria-expanded]="open()"
                [attr.aria-activedescendant]="active() >= 0 ? 'vz-gs-' + active() : null"
                [attr.aria-label]="'search.label' | t"
                [placeholder]="'search.placeholder' | t"
                [value]="query()"
                (input)="onInput($event)"
                (focus)="onFocus()"
                (keydown)="onKey($event)"
            />
            @if (query()) {
                <button type="button" class="vz-gsearch__x" (click)="clear()" [attr.aria-label]="'search.clear' | t">
                    <vz-icon name="x" size="s" />
                </button>
            }
        </div>
        @if (open()) {
            <div class="vz-pop vz-gsearch__pop" id="vz-gsearch-list" role="listbox" [attr.aria-label]="'search.results' | t">
                @for (group of view(); track group.code) {
                    <div class="vz-gsearch__h" role="presentation">{{ 'search.groups.' + group.code | t }}</div>
                    @for (row of group.rows; track row.index) {
                        <a
                            class="vz-gsearch__r"
                            role="option"
                            [id]="'vz-gs-' + row.index"
                            [attr.aria-selected]="row.index === active()"
                            [class.is-active]="row.index === active()"
                            [routerLink]="row.item.link"
                            [queryParams]="row.item.queryParams"
                            (click)="close()"
                            (mouseenter)="active.set(row.index)"
                        >
                            <span class="vz-gsearch__i" aria-hidden="true"><vz-icon [name]="icon(group.code)" size="s" /></span>
                            <span class="vz-gsearch__t">
                                <b>{{ row.item.title }} <vz-owner [owner]="row.item.owner" /></b>
                                @if (row.item.subtitle) {
                                    <small>{{ row.item.subtitle }}</small>
                                }
                            </span>
                        </a>
                    }
                } @empty {
                    <p class="vz-gsearch__empty">{{ 'search.empty' | t: { q: query().trim() } }}</p>
                }
            </div>
        }
        <span class="vz-sr" aria-live="polite">{{ live() }}</span>
    `,
})
export class VzGlobalSearchComponent {
    private readonly store = inject(PanelStore);
    private readonly texts = inject(TextService);
    private readonly router = inject(Router);
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');

    /** Wyniki otwarte — powłoka zamyka wtedy dzwonek i menu konta. */
    readonly opened = output<void>();

    protected readonly query = signal('');
    protected readonly focused = signal(false);
    protected readonly active = signal(-1);

    private readonly labels: SearchLabels = {
        policy: (n) => (n ? this.texts.t('documents.source.policy', { n }) : this.texts.t('documents.source.policyNoNumber')),
        financing: (n) => (n ? this.texts.t('documents.source.financing', { n }) : this.texts.t('documents.source.financingNoNumber')),
        case: (id) => this.texts.t('documents.source.case', { id }),
        caseTitle: (id, title) => this.texts.t('search.caseTitle', { id, title }),
        services: (services, renewal) =>
            [...services, ...(renewal ? ['renewal'] : [])].map((s) => this.texts.t(`caseType.${s}`)).join(' + '),
        policyNumber: (n) => this.texts.t('search.policyNumber', { n }),
        contractNumber: (n) => this.texts.t('search.contractNumber', { n }),
        contractPreparing: this.texts.t('search.contractPreparing'),
        date: (value) => when(value),
    };

    private readonly groups = computed(() =>
        search(
            this.query(),
            {
                vehicles: this.store.vehicles() ?? [],
                policies: this.store.policies() ?? [],
                financing: this.store.financing() ?? [],
                cases: this.store.cases() ?? [],
                tickets: this.store.tickets() ?? [],
                documents: this.store.documents() ?? [],
            },
            this.labels
        )
    );

    /** Grupy z numerem wiersza w całej liście (↑ ↓ przechodzą przez grupy). */
    protected readonly view = computed(() => {
        let index = 0;

        return this.groups().map((g) => ({ code: g.code, rows: g.items.map((item) => ({ item, index: index++ })) }));
    });

    private readonly items = computed<SearchItem[]>(() => this.groups().flatMap((g) => g.items));

    protected readonly open = computed(() => this.focused() && this.query().trim().length >= SEARCH_MIN);

    protected readonly live = computed(() => (this.open() ? this.texts.t('search.count', { n: this.items().length }) : ''));

    icon(code: SearchGroupCode): VzIconName {
        return ICONS[code];
    }

    onFocus(): void {
        this.focused.set(true);
        // Dokumenty wczytujemy dopiero, gdy klient zaczyna szukać
        this.store.loadDocuments().subscribe();

        if (this.open()) {
            this.opened.emit();
        }
    }

    onInput(event: Event): void {
        this.query.set((event.target as HTMLInputElement).value);
        this.focused.set(true);
        this.active.set(-1);

        if (this.open()) {
            this.opened.emit();
        }
    }

    onKey(event: KeyboardEvent): void {
        const count = this.items().length;

        switch (event.key) {
            case 'ArrowDown':
                if (this.open() && count) {
                    event.preventDefault();
                    this.active.set((this.active() + 1) % count);
                    this.scrollActive();
                }
                break;
            case 'ArrowUp':
                if (this.open() && count) {
                    event.preventDefault();
                    this.active.set(this.active() <= 0 ? count - 1 : this.active() - 1);
                    this.scrollActive();
                }
                break;
            case 'Enter': {
                const item = this.items()[Math.max(this.active(), 0)];

                if (this.open() && item) {
                    event.preventDefault();
                    this.router.navigate(item.link, { queryParams: item.queryParams ?? undefined });
                    this.close();
                }
                break;
            }
            case 'Escape':
                if (this.open() || this.query()) {
                    event.stopPropagation();
                    this.clear();
                }
                break;
        }
    }

    clear(): void {
        this.query.set('');
        this.active.set(-1);
        this.input().nativeElement.focus();
    }

    /** Po przejściu do wyniku: puste pole, bez fokusu. */
    close(): void {
        this.query.set('');
        this.active.set(-1);
        this.focused.set(false);
        this.input().nativeElement.blur();
    }

    onDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.focused.set(false);
            this.active.set(-1);
        }
    }

    /** Tab poza pole i wyniki zamyka listę. */
    onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;

        if (next && !this.host.nativeElement.contains(next)) {
            this.focused.set(false);
            this.active.set(-1);
        }
    }

    private scrollActive(): void {
        setTimeout(() => this.host.nativeElement.querySelector(`#vz-gs-${this.active()}`)?.scrollIntoView({ block: 'nearest' }));
    }
}
